import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  IconButton,
  Grid,
  Tab,
  Divider,
  DialogActions,
  Button,
} from "@material-ui/core";
import { TabContext, TabList, TabPanel } from "@material-ui/lab";
import Api from "lib/api/httpRequestHelper";
import MaskAndValid from "utils/maskAndValid";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import PedidoDetailsItems from "./pedido-details-items";
import PedidoDetailsNFs from "./pedido-details-nf";
import PedidoDetailsOds from "./pedido-details-od";
import InvoicingRulesDisplay from "components/InvoicingRules/InvoicingRulesDisplay";
import { Close, Receipt } from "@mui/icons-material";
import LoadingData from "components/Loading/LoadingData";

export default function DialogPedidoDetails({ open, onClose, orderId }) {
  const [orderDetails, setOrderDetails] = useState(null);
  const [invoicingRules, setInvoicingRules] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(1);
  const snackbar = useSnackbar();

  const handleSearch = async () => {
    setLoading(true);
    await getOrderDetails();
    await getInvoicingRules();
    setLoading(false);
  };

  const getOrderDetails = async () => {
    try {
      const response = await Api.get(
        "list-orders-store",
        "sellinorder",
        "?numeroPedido=" + orderId,
        true
      );
      setOrderDetails(response?.Result?.ListPedidos[0]);
    } catch (error) {
      setOrderDetails(null);
      snackbar.showMessage("Erro ao buscar detalhes do pedido", {
        severity: "error",
      });
    }
  };

  const getInvoicingRules = async () => {
    try {
      const response = await Api.get(
        "order-fat",
        "invoice",
        "?numeroPedido=" + orderId
      );
      setInvoicingRules(response.Result);
    } catch (error) {
      snackbar.showMessage("Erro ao buscar regras de faturamento", {
        severity: "error",
      });
    }
  };

  useEffect(() => {
    handleSearch();
  }, [orderId]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl">
      <DialogTitle>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h3" color="primary">
            <Receipt fontSize="small" />
            Detalhes do Pedido {orderId}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent>
        {loading && (
          <Grid item xs={12}>
            <LoadingData
              searching={loading}
              label="Buscando detalhes do pedido..."
            />
          </Grid>
        )}
        {!loading && (
          <Grid container spacing={2} style={{ marginBottom: "1.5rem" }}>
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={6} md={4}>
                  <Typography variant="h4" color="primary">
                    Nr Pedido
                  </Typography>
                  <Typography color="textPrimary">
                    {orderDetails?.NumeroPedido}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={4}>
                  <Typography variant="h4" color="primary">
                    Nr Proposta
                  </Typography>
                  <Typography color="textPrimary">
                    {orderDetails?.NumeroProposta}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={4}>
                  <Typography variant="h4" color="primary">
                    Status
                  </Typography>
                  <Typography color="textPrimary">
                    {orderDetails?.StatusPedido}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={4}>
                  <Typography variant="h4" color="primary">
                    Data do Pedido
                  </Typography>
                  <Typography color="textPrimary">
                    {MaskAndValid.formattDateBR(orderDetails?.DtPedido)}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={4}>
                  <Typography variant="h4" color="primary">
                    Valor Faturado
                  </Typography>
                  <Typography color="textPrimary">
                    R$ {MaskAndValid.maskPrice(orderDetails?.ValorFaturado)}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={4}>
                  <Typography variant="h4" color="primary">
                    Data Entrega Desejada
                  </Typography>
                  <Typography color="textPrimary">
                    {MaskAndValid.formattDateBR(
                      orderDetails?.Itens[0]?.DataEntregaDesejadaLojista
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={4}>
                  <Typography variant="h4" color="primary">
                    Data Prevista Od
                  </Typography>
                  <Typography color="textPrimary">
                    {MaskAndValid.formattDateBR(
                      orderDetails?.Itens[0]?.DataPrevistaOd
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={4}>
                  <Typography variant="h4" color="primary">
                    Data Prevista Faturamento
                  </Typography>
                  <Typography color="textPrimary">
                    {MaskAndValid.formattDateBR(
                      orderDetails?.Itens[0]?.DataPrevistaFaturamento
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={4}>
                  <Typography variant="h4" color="primary">
                    Data Entrega
                  </Typography>
                  <Typography color="textPrimary">
                    {MaskAndValid.formattDateBR(
                      orderDetails?.Itens[0]?.DataEntrega
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={4}>
                  <Typography variant="h4" color="primary">
                    Representante
                  </Typography>
                  <Typography color="textPrimary">
                    {orderDetails?.NomeRepresentante}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={4}>
                  <Typography variant="h4" color="primary">
                    Transportadora
                  </Typography>
                  <Typography color="textPrimary">
                    {[...new Set(orderDetails?.ODs?.map((od) => od.NomeTransportadoraDespacho).filter(Boolean))].join(", ") || "-"}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={4}>
                  <Typography variant="h4" color="primary">
                    Email do Cliente
                  </Typography>
                  <Typography color="textPrimary">
                    {orderDetails?.EmailCliente}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <TabContext value={tab} width="100%">
              <TabList
                variant="scrollable"
                scrollButtons="false"
                onChange={(_, newValue) => {
                  setTab(newValue);
                }}
                centered
              >
                <Tab label="Itens" value={1} />
                <Tab label="Notas Fiscais" value={2} />
                <Tab label="ODs" value={3} />
                <Tab label="Regras de Faturamento" value={4} />
              </TabList>

              <Grid item xs={12}>
                <Divider />
              </Grid>

              <Grid item xs={12}>
                <TabPanel value={1}>
                  <PedidoDetailsItems items={orderDetails?.Itens} />
                </TabPanel>
                <TabPanel value={2}>
                  <PedidoDetailsNFs NFs={orderDetails?.NFs} />
                </TabPanel>
                <TabPanel value={3}>
                  <PedidoDetailsOds ods={orderDetails?.ODs} />
                </TabPanel>
                <TabPanel value={4}>
                  <InvoicingRulesDisplay invoicingRules={invoicingRules} />
                </TabPanel>
              </Grid>
            </TabContext>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="primary" onClick={onClose}>
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
